import { AbstractBuildstep } from "@project/emerge";
import type {
  AbstractPartDefinition,
  DragConstraint,
  DragUpdateResult,
  SnapAnchor,
} from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import { getPianoHingeSegments } from "@project/pricing";
import filter from "lodash/filter";
import flatten from "lodash/flatten";
import groupBy from "lodash/groupBy";
import last from "lodash/last";
import map from "lodash/map";
import mapValues from "lodash/mapValues";
import * as THREE from "three"; // Use namespace import
import { Alignment } from "../enum/Alignment";
import { ConnectorCategory } from "../enum/ConnectorType";
import { EdgeType } from "../enum/EdgeType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { MiterType } from "../enum/MiterType";
import { OrientationOnRect } from "../enum/OrientationOnRect";
import { LTPartType } from "../enum/PartType";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { ConnectorSetInfos } from "../interfaces/ConnectorSetInfos";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { HingeType } from "../interfaces/HingeType";
import type { LockType } from "../interfaces/LockType";
import type { PianoHingeType } from "../interfaces/PianoHingeType";
import type { ProfileProperties } from "../interfaces/ProfileProperties";
import type { PianoHingeDefinition } from "../parts/PianoHingeDefinition";
import type { ProfileDefinition } from "../parts/ProfileDefinition";
import { EdgeInfos } from "../structure/EdgeInfos";
import { DefaultGroupName } from "../structure/Rect";
import { RectPropertiesBySide } from "../structure/RectProperties";
import type { VariableOptions } from "../types/VariableOptions";
import { OptionDBInstance } from "../user_options/options_db";
import type { HingeDefinition } from "./HingeDefinition";
import type { LockDefinition } from "./LockDefinition";

function isConnectorSetInfos(o: VariableOptions): o is ConnectorSetInfos {
  return (
    o.variableOptionType === VariableOptionType.HINGE_SET ||
    o.variableOptionType === VariableOptionType.LOCK_SET
  );
}

/** Default Klavierband width in mm when option has width 0 (6 segments = 6 × 25.4, matches old mesh clone size). */
const DEFAULT_PIANO_HINGE_WIDTH_MM = 152.4;

/** Resolve piano hinge option from Klavierband (PianoHingeType) options. */
function getPianoHingeOption():
  | (PianoHingeType & {
      meshes?: Partial<Record<"top" | "bottom", unknown>>;
    })
  | null {
  const fromPianoHingeKind = OptionDBInstance.getAllOfKind<PianoHingeType>(
    LTCaseUserOption.PianoHingeType,
  );
  if (fromPianoHingeKind && fromPianoHingeKind.length > 0) {
    return fromPianoHingeKind[0] as PianoHingeType & {
      meshes?: Partial<Record<"top" | "bottom", unknown>>;
    };
  }
  return null;
}

// No local core type; we intersect concrete definitions with AbstractPartDefinition

// --- Dependency Data Interfaces ---

interface BaseProfileStepDependency {
  partId: string;
  groupName: string;
  buildstepName: string; // Keep buildstep name for easy assignment
}

interface ConnectorDependencyData extends BaseProfileStepDependency {
  partKind: "Connector";
  type: LTPartType.Hinge | LTPartType.Lock;
  connectorType: ConnectorCategory; // Hinge or Lock
  partSelection: HingeType | LockType; // Virtual option with specific mesh
  position: THREE.Vector3; // Pre-offset position
  flip: boolean;
  isTop: boolean;
  hasBothMeshes: boolean; // Whether the original connector had both top and bottom meshes
  edgeSide: Sides;
  edgeIsVertical: boolean;
  edgeIndex: number;
  edgeInverted: boolean;
  edgeNormalDir: THREE.Vector3; // Needed for final offset calc
  // Settings needed for final calculations
  settingsProfileStrength: number;
  settingsProfileExtra: number;
  settingsProfileInverted: boolean;
  // Drag constraint data
  sourceOptionUuid: string; // Links back to variableOptions entry
  edgeStartPos: THREE.Vector3; // Edge start for drag bounds
  edgeEndPos: THREE.Vector3; // Edge end for drag bounds
  originalEdgeStartPos: THREE.Vector3; // Original edge start for margin calculation
  originalEdgeEndPos: THREE.Vector3; // Original edge end for margin calculation
  edgeDirection: THREE.Vector3; // Edge direction for drag axis
  edgeLength: number; // Edge length for position calculation
  originalEdgeLength: number; // Original edge length for margin calculation
  n: number; // Interpolation factor
  allConnectorPositions: THREE.Vector3[]; // Other connector positions for snap anchors
  // Angle section padding (to prevent overlap)
  angleSectionDepth: number;
}

/** Dependency data for one piano-hinge part (top or bottom). Same orientation/side/rotation as hinges; renderer places mesh at each position. */
interface PianoHingeSegmentDependencyData extends BaseProfileStepDependency {
  partKind: "PianoHingeSegment";
  positions: THREE.Vector3[];
  totalSegments: number;
  totalLength: number;
  invert: boolean;
  /** Same as hinges: edge side for rotation (edge.rects[0].side). */
  edgeSide: Sides;
  edgeDir: "x" | "y";
  settingsProfileStrength: number;
  edgeIsVertical: boolean;
  isTop: boolean;
  edgeIndex: number;
  flip: boolean;
}

interface ProfileDependencyData extends BaseProfileStepDependency {
  partKind: "Profile";
  type: LTPartType.Profile;
  points: THREE.Vector3[];
  cutReference: Sides; // Side
  inverse: boolean; // from edge.inverted
  miter: MiterType;
  partSelection: ProfileProperties["partSelection"]; // From settings
  // Drag constraint data for closing profiles
  cutGroupName: string | null; // Links to settings.cuts entry
  cutNormal: THREE.Vector3 | null; // Direction perpendicular to edge (for drag axis)
  edgeCenter: THREE.Vector3; // Center of the edge for drag bounds
  edgeDirection: THREE.Vector3; // Direction along the edge
  edgeLength: number; // Length of the edge
  currentCutOffset: number; // Current cut offset for reference
  profileExtra: number; // Profile extra setting
  // Minimum offset from case border for closing profiles (in mm)
  minOffsetFromBorder: number;
  // Maximum offset based on case geometry (in mm)
  maxCutOffset: number;
}

type ProfileStepDependencyData =
  | ConnectorDependencyData
  | PianoHingeSegmentDependencyData
  | ProfileDependencyData;

// --- Refactored Build Step ---

export class ProfileBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  AbstractPartDefinition<string>,
  ProfileStepDependencyData // Specify the dependency data type
> {
  constructor() {
    super("ProfileBuilder"); // Consistent name
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, ProfileStepDependencyData> {
    const dependencies = new Map<string, ProfileStepDependencyData>();
    let connectorCounter = 0;
    let pianoHingeCounter = 0;
    let profileCounter = 0;

    // build index to get edges by groupname and cutReference
    const groupedByGroup = groupBy(blueprint.rects, "group");
    const edgesByGroupAndReference = mapValues(groupedByGroup, (rects) =>
      groupBy(
        filter(flatten(map(rects, (r) => r.edges)), {
          type: EdgeType.FROMCUT,
        }),
        "cutReference",
      ),
    );

    const {
      strength: profileStrength,
      extra: profileExtra,
      inverted: profileInverted,
    } = settings.exteriorOptions.profiles;

    // Get angle section depth for constraint padding
    const angleSectionDepth =
      settings.exteriorOptions.angleSections?.depth ?? 0;

    // Minimum offset from case border for closing profiles (in mm)
    const minOffsetFromBorder = 50;

    // Klavierband width for spacing: parts sit directly next to each other (Breite = Abstand)
    const pianoHingeOption = getPianoHingeOption();
    const pianoHingeWidthMm =
      pianoHingeOption && pianoHingeOption.width > 0
        ? pianoHingeOption.width
        : DEFAULT_PIANO_HINGE_WIDTH_MM;

    // --- Helper Functions (Adapted from execute) ---

    type EdgeConnector = EdgeInfos["connectors"][number];

    const meshKeyForConnector = (
      connector: EdgeConnector,
      isTop: boolean,
    ): "top" | "bottom" => {
      if (connector.flip) {
        return isTop ? "bottom" : "top";
      }
      return isTop ? "top" : "bottom";
    };

    const resolveConnectorMesh = (
      connector: EdgeConnector,
      meshKey: "top" | "bottom",
    ): unknown => {
      return (
        connector.partSelection.meshes?.[meshKey] ||
        connector.partSelection.meshes?.[meshKey === "top" ? "bottom" : "top"]
      );
    };

    const createConnectorDependency = (
      edge: EdgeInfos,
      connector: EdgeConnector,
      groupName: string,
      partId: string,
      angleSectionPadding: number,
      allConnectorPositions: THREE.Vector3[],
    ): ConnectorDependencyData | null => {
      const cutRef = edge.cut?.reference;
      if (cutRef === undefined) return null;

      const isTop = cutRef !== edge.cutReference;
      const finalMesh = resolveConnectorMesh(
        connector,
        meshKeyForConnector(connector, isTop),
      );

      if (!finalMesh) {
        console.warn(`Connector ${connector.partSelection.name} has no meshes`);
        return null;
      }

      const firstRect = edge.rects[0];
      if (!firstRect) return null;

      const meshRecord = finalMesh as Record<string, unknown>;
      const selectionRecord = connector.partSelection as unknown as Record<
        string,
        unknown
      >;
      const finalPartSelection = {
        ...connector.partSelection,
        meshes: { default: finalMesh },
        cutouts: meshRecord.cutouts ?? selectionRecord.cutouts,
      };

      return {
        partId,
        partKind: "Connector",
        groupName,
        buildstepName: this.buildstepName,
        type:
          connector.type === ConnectorCategory.Hinge
            ? LTPartType.Hinge
            : LTPartType.Lock,
        connectorType: connector.type,
        partSelection: finalPartSelection as HingeType | LockType,
        position: connector.position.clone(),
        flip: connector.flip,
        isTop,
        hasBothMeshes: false,
        edgeSide: firstRect.side,
        edgeIsVertical: edge.orientationOnRect === OrientationOnRect.VERTICAL,
        edgeIndex: edge.index,
        edgeInverted: edge.inverted,
        edgeNormalDir: edge.inwards.clone(),
        settingsProfileStrength: profileStrength,
        settingsProfileExtra: profileExtra,
        settingsProfileInverted: profileInverted,
        sourceOptionUuid: connector.sourceOptionUuid,
        edgeStartPos: edge.start.outerPos.clone(),
        edgeEndPos: edge.end.outerPos.clone(),
        originalEdgeStartPos: edge.start.originalOuterPos.clone(),
        originalEdgeEndPos: edge.end.originalOuterPos.clone(),
        edgeDirection: edge.direction.clone(),
        edgeLength: edge.outerLength,
        originalEdgeLength: edge.originalEdgeLength,
        n: connector.n,
        allConnectorPositions,
        angleSectionDepth: angleSectionPadding,
      };
    };

    const calculateConnectorDependencies = (
      edges: EdgeInfos[],
      groupName: string,
      angleSectionPadding: number,
    ): ConnectorDependencyData[] => {
      const connectorDeps: ConnectorDependencyData[] = [];
      for (const edge of edges) {
        const allConnectorPositions = edge.connectors.map((c) =>
          c.position.clone(),
        );

        for (const [i, connector] of edge.connectors.entries()) {
          if (!connector) continue;
          const partId = `connector_${groupName}_${edge.cutReference}_${edge.index}_${i}_${connectorCounter++}`;
          const dep = createConnectorDependency(
            edge,
            connector,
            groupName,
            partId,
            angleSectionPadding,
            allConnectorPositions,
          );
          if (!dep) continue;
          connectorDeps.push(dep);
        }
      }
      return connectorDeps;
    };

    const collectPianoHingeSegmentData = (
      connectors: EdgeConnector[],
      bandWidthMm: number,
    ): {
      firstConnector: EdgeConnector | null;
      segmentData: { length: number; position: THREE.Vector3 }[];
    } => {
      const firstConn = connectors[0];
      if (firstConn?.type !== ConnectorCategory.Hinge) {
        return { firstConnector: null, segmentData: [] };
      }

      let firstConnector: EdgeConnector | null = null;
      const segmentData: { length: number; position: THREE.Vector3 }[] = [];
      const phConnectors = filter(
        connectors,
        (c) => (c.partSelection as HingeType).pianoHinge === true,
      );

      for (let c = 0; c < phConnectors.length - 1; c++) {
        const connector1 = phConnectors[c];
        const connector2 = phConnectors[c + 1];
        if (!connector1 || !connector2) continue;
        firstConnector ??= connector1;

        const width =
          (connector1.partSelection as HingeType).width > 0
            ? (connector1.partSelection as HingeType).width
            : 0;
        const pairLength =
          connector1.position.distanceTo(connector2.position) - width - 20;
        const nParts = Math.max(1, Math.floor(pairLength / bandWidthMm));
        const gapCenter = connector1.position
          .clone()
          .lerp(connector2.position, 0.5);
        const edgeDirVec = connector2.position
          .clone()
          .sub(connector1.position)
          .normalize();

        for (let i = 0; i < nParts; i++) {
          let thisPartLength = pairLength;
          if (nParts > 1) {
            thisPartLength =
              i < nParts - 1
                ? bandWidthMm
                : pairLength - (nParts - 1) * bandWidthMm;
          }
          const position = gapCenter
            .clone()
            .add(
              edgeDirVec
                .clone()
                .multiplyScalar((i - (nParts - 1) / 2) * bandWidthMm),
            );
          segmentData.push({ length: thisPartLength, position });
        }
      }

      return { firstConnector, segmentData };
    };

    const calculatePianoHingeDependencies = (
      sortedEdges: EdgeInfos[],
      bandWidthMm: number,
    ): PianoHingeSegmentDependencyData[] => {
      const hingeDeps: PianoHingeSegmentDependencyData[] = [];
      for (const edge of sortedEdges) {
        const { connectors, group } = edge;
        const firstRect = edge.rects[0];
        if (!firstRect) continue;
        const edgeSide = firstRect.side;
        const { firstConnector, segmentData } = collectPianoHingeSegmentData(
          connectors,
          bandWidthMm,
        );
        if (segmentData.length === 0) continue;
        const totalLength = segmentData.reduce((s, d) => s + d.length, 0);
        const totalSegments = getPianoHingeSegments(totalLength);
        const positions = segmentData.map((d) => d.position.clone());
        const flip = firstConnector?.flip ?? false;
        const cutRef = edge.cut?.reference;
        if (cutRef === undefined) continue;
        const isTop = cutRef !== edge.cutReference;
        // Old logic: one part per edge (mesh = top or bottom from isTop), not two parts per edge
        const base: Omit<
          PianoHingeSegmentDependencyData,
          | "partId"
          | "partKind"
          | "positions"
          | "totalSegments"
          | "totalLength"
          | "isTop"
        > = {
          groupName: group,
          buildstepName: this.buildstepName,
          invert: group === DefaultGroupName,
          edgeSide,
          edgeDir: edge.dir === "y" ? "y" : "x",
          settingsProfileStrength: profileStrength,
          edgeIsVertical: edge.orientationOnRect === OrientationOnRect.VERTICAL,
          edgeIndex: edge.index,
          flip,
        };
        hingeDeps.push({
          ...base,
          partId: `pianohinge_${group}_${edgeSide}_${isTop ? "top" : "bottom"}_${pianoHingeCounter++}`,
          partKind: "PianoHingeSegment",
          positions,
          totalSegments,
          totalLength,
          isTop,
        });
      }
      return hingeDeps;
    };

    // Helper: Calculate cut normal direction
    const calculateCutNormal = (
      cut: { reference?: Sides } | null,
      inverted: boolean,
    ): THREE.Vector3 | null => {
      if (cut?.reference === undefined) return null;
      const refProps = RectPropertiesBySide[cut.reference];
      if (!refProps) return null;

      const normal = refProps.normalDir.clone();
      // Negate unless inverted (e.g., cover on Haube)
      if (!inverted) normal.negate();
      return normal;
    };

    // Helper: Determine miter type for a segment
    const determineMiterType = (
      segPoints: THREE.Vector3[],
      sortedEdges: EdgeInfos[],
      isClosedLoop: boolean,
    ): MiterType => {
      if (isClosedLoop) return MiterType.NONE;

      const firstEdge = sortedEdges[0];
      const lastEdge = last(sortedEdges);
      if (!firstEdge || !lastEdge) return MiterType.NONE;

      const startOfChain = firstEdge.start.outerPos;
      const endOfChain = lastEdge.end.outerPos;

      const sp0 = segPoints[0];
      const spL = segPoints.at(-1);
      if (!sp0 || !spL) return MiterType.NONE;

      const isStartOfChain = sp0.distanceTo(startOfChain) < 0.1;
      const isEndOfChain = spL.distanceTo(endOfChain) < 0.1;

      if (isStartOfChain && isEndOfChain) return MiterType.BOTH;
      if (isStartOfChain) return MiterType.START;
      if (isEndOfChain) return MiterType.END;
      return MiterType.NONE;
    };

    interface AtomicSegment {
      points: THREE.Vector3[];
      startsAtEdgeStart: boolean;
      endsAtEdgeEnd: boolean;
      sourceEdge: EdgeInfos; // Track source edge for drag data
    }

    interface ProfileDependencyContext {
      sortedEdges: EdgeInfos[];
      groupName: string;
      inverted: boolean;
      minOffset: number;
      isClosedLoop: boolean;
      cutGroupName: string | null;
      cutNormal: THREE.Vector3 | null;
      currentCutOffset: number;
      maxCutOffset: number;
    }

    const calculateMaxCutOffset = (
      cutNormal: THREE.Vector3 | null,
      minOffset: number,
    ): number => {
      if (!cutNormal || !blueprint.innerDimensions) {
        return 500; // Fallback
      }
      const dims = blueprint.innerDimensions;
      const depthInCutDirection = Math.abs(
        dims.x * cutNormal.x + dims.y * cutNormal.y + dims.z * cutNormal.z,
      );
      return depthInCutDirection - minOffset;
    };

    const generateAtomicSegments = (
      sortedEdges: EdgeInfos[],
    ): AtomicSegment[] => {
      const allSegments: AtomicSegment[] = [];

      for (const edge of sortedEdges) {
        const connectors = edge.cuttingConnectors;
        for (let i = 0; i <= connectors.length; i++) {
          const pStart = edge.getCutPosition(i, false);
          const pEnd = edge.getCutPosition(i + 1, true);

          if (pStart.distanceTo(pEnd) < 0.1) {
            continue;
          }

          allSegments.push({
            points: [pStart, pEnd],
            startsAtEdgeStart: i === 0,
            endsAtEdgeEnd: i === connectors.length,
            sourceEdge: edge,
          });

          if (allSegments.length > 500) {
            console.error(
              `ProfileBuildStep: too many segments (${allSegments.length}), aborting edge`,
            );
            break;
          }
        }
      }

      return allSegments;
    };

    const stitchSegments = (allSegments: AtomicSegment[]): AtomicSegment[] => {
      if (allSegments.length === 0) {
        return [];
      }

      const mergedSegments: AtomicSegment[] = [];
      const firstSeg = allSegments[0];
      if (!firstSeg) {
        return [];
      }
      let current = firstSeg;

      for (let k = 1; k < allSegments.length; k++) {
        const next = allSegments[k];
        if (!next) continue;

        if (current.endsAtEdgeEnd && next.startsAtEdgeStart) {
          current.points.push(...next.points.slice(1));
          current.endsAtEdgeEnd = next.endsAtEdgeEnd;
        } else {
          mergedSegments.push(current);
          current = next;
        }
      }
      mergedSegments.push(current);

      return mergedSegments;
    };

    const handleClosedLoopWrapAround = (
      mergedSegments: AtomicSegment[],
      isClosedLoop: boolean,
    ): AtomicSegment[] => {
      if (!isClosedLoop || mergedSegments.length <= 1) {
        return mergedSegments;
      }

      const first = mergedSegments[0];
      const lastSeg = mergedSegments.at(-1);
      if (!first || !lastSeg) {
        return mergedSegments;
      }

      if (lastSeg.endsAtEdgeEnd && first.startsAtEdgeStart) {
        lastSeg.points.push(...first.points.slice(1));
        lastSeg.endsAtEdgeEnd = first.endsAtEdgeEnd;
        mergedSegments.shift();
      }

      return mergedSegments;
    };

    const createProfileDependencies = (
      mergedSegments: AtomicSegment[],
      context: ProfileDependencyContext,
    ): ProfileDependencyData[] => {
      const profileDeps: ProfileDependencyData[] = [];
      const firstEdge = context.sortedEdges[0];
      if (!firstEdge) return profileDeps;

      const side = firstEdge.cutReference;

      for (const [idx, seg] of mergedSegments.entries()) {
        const miterType = determineMiterType(
          seg.points,
          context.sortedEdges,
          context.isClosedLoop,
        );
        const partId = `profile_${context.groupName}_${side}_${idx}_${profileCounter++}`;

        const segStart = seg.points[0];
        const segEnd = seg.points.at(-1);
        if (!segStart || !segEnd) continue;
        const edgeCenter = segStart.clone().add(segEnd).multiplyScalar(0.5);
        const edgeDirection = segEnd.clone().sub(segStart).normalize();
        const edgeLength = segStart.distanceTo(segEnd);

        const dep: ProfileDependencyData = {
          partId,
          partKind: "Profile",
          type: LTPartType.Profile,
          groupName: context.groupName,
          buildstepName: this.buildstepName,
          points: seg.points,
          cutReference: side,
          inverse: context.inverted,
          miter: miterType,
          partSelection: settings.exteriorOptions.profiles,
          cutGroupName: context.cutGroupName,
          cutNormal: context.cutNormal,
          edgeCenter,
          edgeDirection,
          edgeLength,
          currentCutOffset: context.currentCutOffset,
          profileExtra,
          minOffsetFromBorder: context.minOffset,
          maxCutOffset: context.maxCutOffset,
        };
        profileDeps.push(dep);
      }

      return profileDeps;
    };

    const calculateProfileDependencies = (
      sortedEdges: EdgeInfos[],
      groupName: string,
      inverted: boolean,
      minOffset: number,
    ): ProfileDependencyData[] => {
      if (!sortedEdges || sortedEdges.length === 0) {
        return [];
      }
      const firstEdge = sortedEdges[0];
      const lastEdge = last(sortedEdges);
      if (!firstEdge || !lastEdge) return [];

      const isClosedLoop = lastEdge.pointsTo(firstEdge);
      const cut = firstEdge.cut;
      const cutGroupName = cut?.groupName ?? null;
      const currentCutOffset = cut?.offset ?? 0;

      const cutNormal = calculateCutNormal(cut, inverted);
      const maxCutOffset = calculateMaxCutOffset(cutNormal, minOffset);

      const allSegments = generateAtomicSegments(sortedEdges);
      if (allSegments.length === 0) {
        return [];
      }

      const mergedSegments = stitchSegments(allSegments);
      const finalSegments = handleClosedLoopWrapAround(
        mergedSegments,
        isClosedLoop,
      );

      return createProfileDependencies(finalSegments, {
        sortedEdges,
        groupName,
        inverted,
        minOffset,
        isClosedLoop,
        cutGroupName,
        cutNormal,
        currentCutOffset,
        maxCutOffset,
      });
    };

    // Helper: Determine edge sets from sorted edges
    const determineEdgeSets = (
      sortedEdges: EdgeInfos[],
      edges: EdgeInfos[],
      groupName: string,
      reference: string,
    ): EdgeInfos[][] => {
      if (sortedEdges.length === edges.length && edges.length > 0) {
        return [sortedEdges];
      }
      if (edges.length > 0) {
        console.warn(
          `Edges in group ${groupName} reference ${reference} are not fully connected or empty. Processing individually.`,
        );
        return edges.map((edge) => [edge]);
      }
      console.warn(
        `No edges found for group ${groupName} reference ${reference}. Skipping.`,
      );
      return [];
    };

    // Helper: Process a single edge set and add dependencies
    const processEdgeSet = (
      currentEdgeSet: EdgeInfos[],
      groupName: string,
      inverted: boolean,
    ): void => {
      const connectorDeps = calculateConnectorDependencies(
        currentEdgeSet,
        groupName,
        angleSectionDepth,
      );
      const pianoHingeDeps = calculatePianoHingeDependencies(
        currentEdgeSet,
        pianoHingeWidthMm,
      );
      const profileDeps = calculateProfileDependencies(
        currentEdgeSet,
        groupName,
        inverted,
        minOffsetFromBorder,
      );

      connectorDeps.forEach((dep) => {
        dependencies.set(dep.partId, dep);
      });
      pianoHingeDeps.forEach((dep) => {
        dependencies.set(dep.partId, dep);
      });
      profileDeps.forEach((dep) => {
        dependencies.set(dep.partId, dep);
      });
    };

    // --- Main Loop ---
    let totalIterations = 0;
    const maxTotalIterations = 1000; // Safety limit
    let shouldAbort = false;

    for (const groupName in edgesByGroupAndReference) {
      if (shouldAbort) break;
      for (const reference in edgesByGroupAndReference[groupName]) {
        if (++totalIterations > maxTotalIterations) {
          console.error(
            `ProfileBuildStep: exceeded max iterations (${maxTotalIterations}), aborting`,
          );
          shouldAbort = true;
          break;
        }

        const edgeList = edgesByGroupAndReference[groupName]?.[reference];
        if (!edgeList || edgeList.length === 0) continue;

        const firstEdgeEntry = edgeList[0];
        const inverted = firstEdgeEntry?.inverted ?? false;
        const sortedEdges = EdgeInfos.sortConnectedEdges(edgeList);
        const edgeSets = determineEdgeSets(
          sortedEdges,
          edgeList,
          groupName,
          reference,
        );

        for (const currentEdgeSet of edgeSets) {
          processEdgeSet(currentEdgeSet, groupName, inverted);
        }
      }
    }

    return dependencies;
  }

  public createPart(
    partId: string,
    dependencies: ProfileStepDependencyData,
  ): AbstractPartDefinition<string> {
    switch (dependencies.partKind) {
      case "Connector":
        return this.createConnectorPart(partId, dependencies);
      case "PianoHingeSegment":
        return this.createPianoHingeSegmentPart(
          partId,
          dependencies,
        ) as unknown as AbstractPartDefinition<string>;
      case "Profile":
        return this.createProfilePart(partId, dependencies);
      default: {
        const exhaustiveCheck: never = dependencies;
        throw new Error(`Unknown part kind: ${exhaustiveCheck}`);
      }
    }
  }

  private createConnectorPart(
    partId: string,
    dependencies: ConnectorDependencyData,
  ): LockDefinition | HingeDefinition {
    const {
      settingsProfileStrength,
      settingsProfileExtra,
      settingsProfileInverted,
      edgeInverted,
      edgeIsVertical,
      isTop,
      edgeIndex,
      edgeSide,
      partSelection, // Keep the union type here
      // Drag constraint data
      sourceOptionUuid,
      edgeStartPos,
      edgeEndPos,
      originalEdgeStartPos,
      originalEdgeEndPos,
      edgeDirection,
      edgeLength,
      originalEdgeLength,
      n,
      allConnectorPositions,
    } = dependencies;

    // Common calculations
    const e = settingsProfileExtra / 2 - settingsProfileStrength;
    const offset = edgeInverted !== settingsProfileInverted ? -e : e;
    const edgeRectProps = RectPropertiesBySide[edgeSide];
    if (!edgeRectProps) {
      throw new Error(
        `ProfileBuildStep: missing RectPropertiesBySide for edgeSide ${String(edgeSide)}`,
      );
    }
    const outwardOffset = edgeRectProps.normalDir
      .clone()
      .multiplyScalar(settingsProfileStrength);
    let orientation: number = edgeIsVertical ? 90 : 0;

    if (isTop !== edgeIndex < 2) {
      orientation += 180;
    }

    if (dependencies.flip) {
      orientation -= 180;
    }

    // Get connector's own cut size for padding
    const psRec = partSelection as Record<string, unknown>;
    const cutRaw = psRec.cut;
    const connectorCut =
      typeof cutRaw === "number" ? cutRaw : Number(cutRaw) || 0;

    // Shrink edge bounds by angle section depth + half connector's own size
    const totalPadding = dependencies.angleSectionDepth + connectorCut / 2;
    const paddedEdgeStart = edgeStartPos
      .clone()
      .add(edgeDirection.clone().multiplyScalar(totalPadding));
    const paddedEdgeEnd = edgeEndPos
      .clone()
      .sub(edgeDirection.clone().multiplyScalar(totalPadding));

    // Build drag constraint for edge movement
    const dragConstraint: DragConstraint = {
      type: "edge",
      axis: [edgeDirection.x, edgeDirection.y, edgeDirection.z],
      bounds: {
        min: [paddedEdgeStart.x, paddedEdgeStart.y, paddedEdgeStart.z],
        max: [paddedEdgeEnd.x, paddedEdgeEnd.y, paddedEdgeEnd.z],
      },
      snapAnchors: [
        // Center of edge
        {
          position: [
            (edgeStartPos.x + edgeEndPos.x) / 2,
            (edgeStartPos.y + edgeEndPos.y) / 2,
            (edgeStartPos.z + edgeEndPos.z) / 2,
          ],
          label: "Center",
        },
        // Other connectors on same edge (excluding self)
        ...allConnectorPositions
          .filter(
            (pos) => !pos.equals(dependencies.position), // Exclude self
          )
          .map(
            (pos, idx): SnapAnchor => ({
              position: [pos.x, pos.y, pos.z],
              label: `Connector ${idx + 1}`,
            }),
          ),
      ],
      sourceOptionUuid,
    };

    // Settings context for updateSettingsFromDrag
    const settingsContext: Record<string, unknown> = {
      edgeStartPos: [edgeStartPos.x, edgeStartPos.y, edgeStartPos.z],
      edgeEndPos: [edgeEndPos.x, edgeEndPos.y, edgeEndPos.z],
      originalEdgeStartPos: [
        originalEdgeStartPos.x,
        originalEdgeStartPos.y,
        originalEdgeStartPos.z,
      ],
      originalEdgeEndPos: [
        originalEdgeEndPos.x,
        originalEdgeEndPos.y,
        originalEdgeEndPos.z,
      ],
      edgeLength,
      originalEdgeLength,
      edgeDirection: [edgeDirection.x, edgeDirection.y, edgeDirection.z],
      n,
    };

    // Common properties for the return object
    const commonProps = {
      ...dependencies,
      id: partId,
      buildstepName: this.buildstepName,
      group: dependencies.groupName,
      top: dependencies.isTop,
      mesh: (() => {
        const m = (dependencies.partSelection as Record<string, unknown>).mesh;
        return typeof m === "string" ? m : "";
      })(),
      isSupplyPart: true as const,
      position: dependencies.position
        .clone()
        .add(dependencies.edgeNormalDir.clone().multiplyScalar(offset))
        .add(outwardOffset),
      orientation,
      rotation: this.rotateOntoSide(dependencies.edgeSide, orientation),
      scale: 1,
      dontShowInPlan: isTop, // Hide top parts (we only create one part per connector)
      distanceFromPanel: settingsProfileStrength,
      flip: dependencies.flip, // Explicitly include flip property for cutout rotation
      dragConstraint,
      settingsContext,
    };

    if (dependencies.type === LTPartType.Hinge) {
      return {
        ...commonProps,
        type: LTPartType.Hinge,
        partSelection: dependencies.partSelection as HingeType,
      } satisfies HingeDefinition;
    } else if (dependencies.type === LTPartType.Lock) {
      return {
        ...commonProps,
        type: LTPartType.Lock,
        partSelection: dependencies.partSelection as LockType,
      } satisfies LockDefinition;
    } else {
      throw new Error(
        `Invalid dependency type encountered for Connector: ${dependencies.type}`,
      );
    }
  }

  /** Creates one PianoHinge part (top or bottom) for a hinge row. Same rotation/orientation as hinges; part.positions = band positions, renderer places mesh at each. */
  private createPianoHingeSegmentPart(
    partId: string,
    dependencies: PianoHingeSegmentDependencyData,
  ): PianoHingeDefinition {
    const pianoHingeOption = getPianoHingeOption();
    if (!pianoHingeOption) {
      throw new Error(
        "ProfileBuildStep: no Klavierband option found. Add an option under 'Klavierband' in the options library.",
      );
    }
    const effectiveFlip = dependencies.flip;
    let meshKey: "top" | "bottom";
    if (effectiveFlip) {
      meshKey = dependencies.isTop ? "bottom" : "top";
    } else {
      meshKey = dependencies.isTop ? "top" : "bottom";
    }
    const sourceMesh =
      pianoHingeOption.meshes?.[meshKey] ??
      pianoHingeOption.meshes?.[meshKey === "top" ? "bottom" : "top"];
    if (!sourceMesh) {
      throw new Error(
        "ProfileBuildStep: Klavierband option has no meshes (top/bottom).",
      );
    }
    const finalPartSelection: PianoHingeType = {
      ...pianoHingeOption,
      totalSegments: dependencies.totalSegments,
      meshes: { default: sourceMesh },
    } as unknown as PianoHingeType;

    // Same orientation and rotation as hinges (createConnectorPart)
    let orientation: number = dependencies.edgeIsVertical ? 90 : 0;
    if (dependencies.isTop !== dependencies.edgeIndex < 2) {
      orientation += 180;
    }
    if (effectiveFlip) {
      orientation -= 180;
    }
    const pianoEdgeRectProps = RectPropertiesBySide[dependencies.edgeSide];
    if (!pianoEdgeRectProps) {
      throw new Error(
        `ProfileBuildStep: missing RectPropertiesBySide for edgeSide ${String(dependencies.edgeSide)}`,
      );
    }
    const outwardOffset = pianoEdgeRectProps.normalDir
      .clone()
      .multiplyScalar(dependencies.settingsProfileStrength);
    const positions = dependencies.positions.map((p) =>
      p.clone().add(outwardOffset),
    );

    return {
      id: partId,
      buildstepName: this.buildstepName,
      group: dependencies.groupName,
      type: LTPartType.PianoHinge,
      partSelection: finalPartSelection,
      positions,
      segments: dependencies.totalSegments,
      orientation,
      rotation: this.rotateOntoSide(dependencies.edgeSide, orientation),
      scale: 1,
      distanceFromPanel: dependencies.settingsProfileStrength,
      flip: effectiveFlip,
      top: dependencies.isTop,
      isSupplyPart: true,
      dontShowInPlan: dependencies.isTop,
    } satisfies PianoHingeDefinition;
  }

  private createProfilePart(
    partId: string,
    dependencies: ProfileDependencyData,
  ): ProfileDefinition {
    const {
      cutReference,
      points,
      groupName,
      cutGroupName,
      cutNormal,
      edgeCenter,
      edgeDirection,
      edgeLength,
      currentCutOffset,
      profileExtra: depProfileExtra,
      minOffsetFromBorder,
      maxCutOffset,
      inverse,
      ...restDeps
    } = dependencies;
    const side = cutReference;
    const rectProps = RectPropertiesBySide[side];
    if (!rectProps) {
      throw new Error(
        `ProfileBuildStep: missing RectPropertiesBySide for ${String(side)}`,
      );
    }
    const { horizontal, vertical, normal } = rectProps;

    // Calculate center as middle of middle segment in GEOMETRY space
    // This ensures renderer and buildstep use the same centering logic
    // Must match renderer's ccw-based reversal to get the same middle segment
    const ccw = ![Sides.TOP, Sides.BACK, Sides.RIGHT].includes(side);
    const orderedPoints = ccw ? points : [...points].reverse();

    // Transform points to geometry space
    const geoPoints = orderedPoints.map(
      (p) => new THREE.Vector3(p[horizontal], p[vertical], p[normal]),
    );

    // Find middle segment and its midpoint
    const midSegmentIdx = Math.floor((geoPoints.length - 1) / 2);
    const geoCenter = new THREE.Vector3();
    if (geoPoints.length >= 2) {
      const gMid = geoPoints[midSegmentIdx];
      const gMidNext = geoPoints[midSegmentIdx + 1];
      if (gMid && gMidNext) {
        geoCenter.copy(gMid).add(gMidNext).multiplyScalar(0.5);
      }
    } else if (geoPoints.length === 1) {
      const g0 = geoPoints[0];
      if (g0) geoCenter.copy(g0);
    }

    // Transform center back to WORLD space for position
    // geoCenter = (world[horizontal], world[vertical], world[normal])
    // So: world[horizontal] = geoCenter.x, world[vertical] = geoCenter.y, world[normal] = geoCenter.z
    const finalPosition = new THREE.Vector3();
    finalPosition[horizontal] = geoCenter.x;
    finalPosition[vertical] = geoCenter.y;
    finalPosition[normal] = geoCenter.z;

    // Final rotation calculation
    let baseRotation: THREE.Euler;

    if (side === Sides.TOP || side === Sides.BOTTOM) {
      baseRotation = new THREE.Euler(0, 0, 0);
      baseRotation.x -= Math.PI / 2;
      baseRotation.x += THREE.MathUtils.degToRad(180);
    } else {
      baseRotation = new THREE.Euler(0, 0, 0);
      baseRotation.x -= Math.PI;
      baseRotation.y += THREE.MathUtils.degToRad(180);
      baseRotation.z += Math.PI;
    }

    const finalRotation = baseRotation; // Use calculated rotation

    // Build drag constraint for movement perpendicular to the edge (along cut normal)
    // This allows dragging the profile to change the cut offset
    let dragConstraint: DragConstraint | undefined;
    let settingsContext: Record<string, unknown> | undefined;

    if (cutGroupName && cutNormal) {
      // Calculate bounds along the cut normal direction
      // The profile moves along cutNormal to change the cut offset
      // Minimum offset from border is enforced (default 50mm)
      // Maximum offset is calculated from case geometry

      // The "zero offset" position is where the cut would be at the panel edge
      // We move from there along cutNormal by offset amount
      // So: position = zeroPos + cutNormal * offset
      // zeroPos = edgeCenter - cutNormal * currentCutOffset
      const zeroOffsetPos = edgeCenter
        .clone()
        .sub(cutNormal.clone().multiplyScalar(currentCutOffset));

      // Bounds: from minOffsetFromBorder to maxCutOffset
      // These are positions along the cutNormal direction from zeroOffsetPos
      const boundsAtMinOffset = zeroOffsetPos
        .clone()
        .add(cutNormal.clone().multiplyScalar(minOffsetFromBorder));
      const boundsAtMaxOffset = zeroOffsetPos
        .clone()
        .add(cutNormal.clone().multiplyScalar(maxCutOffset));

      dragConstraint = {
        type: "edge",
        axis: [cutNormal.x, cutNormal.y, cutNormal.z],
        bounds: {
          min: [boundsAtMinOffset.x, boundsAtMinOffset.y, boundsAtMinOffset.z],
          max: [boundsAtMaxOffset.x, boundsAtMaxOffset.y, boundsAtMaxOffset.z],
        },
        snapAnchors: [
          // Current position
          {
            position: [edgeCenter.x, edgeCenter.y, edgeCenter.z],
            label: "Current",
          },
        ],
        sourceOptionUuid: cutGroupName, // Use cutGroupName as identifier
      };

      settingsContext = {
        cutGroupName,
        cutNormal: [cutNormal.x, cutNormal.y, cutNormal.z],
        edgeCenter: [edgeCenter.x, edgeCenter.y, edgeCenter.z],
        zeroOffsetPos: [zeroOffsetPos.x, zeroOffsetPos.y, zeroOffsetPos.z],
        edgeDirection: [edgeDirection.x, edgeDirection.y, edgeDirection.z],
        edgeLength,
        currentCutOffset,
        profileExtra: depProfileExtra,
        cutReference: side,
        minOffsetFromBorder,
        maxCutOffset,
        inverse, // For inverted groups, cutNormal is already negated
      };
    }

    const profileResult = {
      ...restDeps, // Includes partId, type, miter, partSelection
      id: partId,
      points: points.map((p) => p.clone()), // Store world points, renderer centers them
      buildstepName: this.buildstepName,
      group: groupName,
      position: finalPosition.clone(),
      orientation: 0, // Profile orientation handled via rotation
      rotation: finalRotation,
      cutReference, // Keep cutReference if needed by renderer
      inverse, // Add back inverse which was destructured separately
      ...(dragConstraint ? { dragConstraint } : {}),
      ...(settingsContext ? { settingsContext } : {}),
    } satisfies ProfileDefinition & AbstractPartDefinition<string>;

    return profileResult;
  }

  /**
   * Convert a drag position change back to settings updates.
   * For connectors, this updates the margin in the variableOptions.
   * For profiles, this updates the cut offset in settings.cuts.
   * Returns both the settings update and display values for UI feedback.
   */
  public updateSettingsFromDrag(
    partId: string,
    newPosition: THREE.Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
    currentSettings: FlightCaseSettings,
  ): DragUpdateResult<FlightCaseSettings> | null {
    if (!constraint.sourceOptionUuid || !settingsContext) {
      return null;
    }

    // Check if this is a profile drag (has cutGroupName in context)
    if (settingsContext.cutGroupName) {
      return this.updateSettingsFromProfileDrag(
        partId,
        newPosition,
        constraint,
        settingsContext,
        currentSettings,
      );
    }

    // Otherwise, handle connector drag
    return this.updateSettingsFromConnectorDrag(
      partId,
      newPosition,
      constraint,
      settingsContext,
      currentSettings,
    );
  }

  /**
   * Handle drag updates for profile parts - updates cut.offset
   */
  private updateSettingsFromProfileDrag(
    _partId: string,
    newPosition: THREE.Vector3,
    _constraint: DragConstraint,
    settingsContext: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const cutGroupName = settingsContext.cutGroupName as string;
    const cutNormal = new THREE.Vector3(
      ...(settingsContext.cutNormal as [number, number, number]),
    );
    const zeroOffsetPos = new THREE.Vector3(
      ...(settingsContext.zeroOffsetPos as [number, number, number]),
    );
    const minOffsetFromBorder =
      (settingsContext.minOffsetFromBorder as number) ?? 50;
    const maxCutOffset = (settingsContext.maxCutOffset as number) ?? 500;

    // Find the cut by group name
    const cutIndex = currentSettings.cuts.findIndex(
      (c) => c.groupName === cutGroupName,
    );

    if (cutIndex === -1) {
      return null;
    }

    const cut = currentSettings.cuts[cutIndex];
    if (!cut) {
      return null;
    }

    // Calculate the new offset based on position along the cut normal
    // newPosition = zeroOffsetPos + cutNormal * newOffset
    // So: newOffset = (newPosition - zeroOffsetPos) . cutNormal
    const movementFromZero = newPosition.clone().sub(zeroOffsetPos);
    const newOffsetRaw = movementFromZero.dot(cutNormal);

    // Enforce min/max offset bounds
    const newOffset = Math.min(
      maxCutOffset,
      Math.max(minOffsetFromBorder, Math.round(newOffsetRaw)),
    );

    // Create updated cuts array
    const updatedCuts = [...currentSettings.cuts];
    updatedCuts[cutIndex] = {
      ...cut,
      offset: newOffset,
    };

    return {
      settingsUpdate: {
        cuts: updatedCuts,
      },
      displayValues: {
        primary: {
          label: "Offset",
          value: newOffset,
          unit: "mm",
        },
      },
    };
  }

  /**
   * Handle drag updates for connector parts - updates margin in variableOptions
   */
  private updateSettingsFromConnectorDrag(
    _partId: string,
    newPosition: THREE.Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
  ): DragUpdateResult<FlightCaseSettings> | null {
    // Find the variable option by UUID
    const optionIndex = currentSettings.variableOptions.findIndex(
      (opt) => opt.uuid === constraint.sourceOptionUuid,
    );

    if (optionIndex === -1) {
      console.warn(
        `Could not find variableOption with UUID ${constraint.sourceOptionUuid}`,
      );
      return null;
    }

    const option = currentSettings.variableOptions[optionIndex];
    if (!option || !isConnectorSetInfos(option)) {
      return null;
    }

    // Extract edge info from settings context
    // We only need direction and original positions for margin calculation
    // Note: edgeDirection and positions are already consistent for all parts
    // sharing the same sourceOptionUuid, so no isTop flip is needed.
    const edgeDir = new THREE.Vector3(
      ...(settingsContext.edgeDirection as [number, number, number]),
    );
    const originalEdgeStart = new THREE.Vector3(
      ...(settingsContext.originalEdgeStartPos as [number, number, number]),
    );
    const originalEdgeEnd = new THREE.Vector3(
      ...(settingsContext.originalEdgeEndPos as [number, number, number]),
    );
    const originalEdgeLength = settingsContext.originalEdgeLength as number;
    const n = (settingsContext.n as number) ?? 0;

    // Calculate margins based on alignment using ORIGINAL edge positions

    // Get the current alignment and count from the option
    const alignment = option.alignment;
    const count = option.count;

    // Get padding from cut
    // Access partSelection via option
    // It is stored as partSelection: LockType | HingeType
    const partSelection = option.partSelection;
    const cutVal = (partSelection as Record<string, unknown>).cut;
    const cut = typeof cutVal === "number" ? cutVal : Number(cutVal) || 0;
    const padding = cut / 2;

    let newMargin: number;

    // Project new position onto edge direction
    // pos = start + dist * dir
    // dist = (pos - start) . dir
    const distFromStart = newPosition
      .clone()
      .sub(originalEdgeStart)
      .dot(edgeDir);
    const distFromEnd = originalEdgeLength - distFromStart; // Approximately

    if (count > 1) {
      // Use formula: m = (pos - Ln - p(1-2n)) / (1-2n)
      // pos here is distance from start
      const L = originalEdgeLength;
      const denom = 1 - 2 * n;

      if (Math.abs(denom) < 0.001) {
        // n=0.5 (center element of odd count?)
        // If n=0.5, formula fails.
        // Logic for center element in START alignment?
        // pos = start + (m + offset + p)
        // offset = (L - 2m - 2p)n = (L - 2m - 2p)0.5 = 0.5L - m - p
        // pos = start + m + 0.5L - m - p + p = start + 0.5L
        // Position is FIXED at center regardless of margin!
        // So dragging it cannot change margin.
        // Return null or current margin?
        return null;
      }

      newMargin = (distFromStart - L * n - padding * denom) / denom;
      newMargin = Math.max(0, newMargin);
    } else if (alignment === Alignment.CENTER) {
      // count == 1
      // CENTER alignment
      // Margin is distance from Center
      const originalCenter = originalEdgeStart
        .clone()
        .add(originalEdgeEnd)
        .multiplyScalar(0.5);
      const distFromCenterVec = newPosition.clone().sub(originalCenter);
      newMargin = distFromCenterVec.dot(edgeDir);
    } else if (alignment === Alignment.END) {
      // END alignment
      // Margin is distance from End (minus padding?)
      // Rect.ts: p = start + (margin + offset + padding) * dir
      // offset = (L - 2m - 2p) * n
      // If END, n=1.
      // offset = L - 2m - 2p.
      // p = start + m + L - 2m - 2p + p = start + L - m - p = end - m - p.
      // m = end - p - pos = (end - pos) - p.
      // distFromEnd is (end - pos).
      newMargin = distFromEnd - padding;
    } else {
      // START alignment (default)
      // n=0.
      // offset = 0.
      // p = start + m + p.
      // m = p - start - p = distFromStart - padding.
      newMargin = distFromStart - padding;
    }

    // Round to reasonable precision
    newMargin = Math.round(newMargin);

    // Create updated variable options array
    const updatedVariableOptions = [...currentSettings.variableOptions];
    updatedVariableOptions[optionIndex] = {
      ...option,
      margin: newMargin,
    } as VariableOptions;

    return {
      settingsUpdate: {
        variableOptions: updatedVariableOptions,
      },
      displayValues: {
        primary: {
          label: "Margin",
          value: newMargin,
          unit: "mm",
        },
      },
    };
  }
}
